import React, { useEffect, useRef } from 'react'
import './otpInput.scss'

interface OtpInputProps {
  /** The current code as a plain string (controlled). */
  value: string
  /** Called with the full joined code on every change. */
  onChange: (value: string) => void
  /** Number of digit boxes. Defaults to 6. */
  length?: number
  /** Focus the first box on mount. */
  autoFocus?: boolean
  disabled?: boolean
}

/**
 * Segmented one-time-code input: `length` single-digit boxes with automatic
 * forward/back focus and paste support. Fully controlled by `value` so callers
 * keep their existing string state and validation untouched.
 */
const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  length = 6,
  autoFocus = false,
  disabled = false
}) => {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = Array.from({ length }, (_, i) => value[i] ?? '')

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus()
  }, [autoFocus])

  const handleChange = (index: number, raw: string) => {
    if (raw && !/^\d$/.test(raw)) return
    const next = [...digits]
    next[index] = raw
    onChange(next.join(''))
    if (raw && index < length - 1) refs.current[index + 1]?.focus()
  }

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text/plain').replace(/\D/g, '')
    if (!pasted) return
    onChange(Array.from({ length }, (_, i) => pasted[i] ?? '').join(''))
    refs.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  return (
    <div className="otp-input">
      {digits.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => {
            refs.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          className={`otp-input__box ${digit ? 'otp-input__box--filled' : ''}`}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  )
}

export default OtpInput
